import React, { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Box, Card, CardHeader, IconButton, List, ListItemIcon, ListItemText, } from '@mui/material';
import { Add, ShareLocation, SubdirectoryArrowRight } from '@mui/icons-material';
import Copy from 'components/common/Copy';
import ShareButton from 'components/common/ShareButton';
import { PortModel } from 'models/PortModel';
import useYards from 'modules/yards/hooks/useYards';
import usePortModals from 'modules/yards/hooks/usePortModals';
import useYardModals from 'modules/yards/hooks/useYardModals';
import StyledListItem from 'modules/yards/common/StyledListItem';
import ViewYardInstructions from 'modules/yards/common/ViewYardInstructions';
import auth from 'recoil/atoms/auth';


type PortsCardProps = {
    title: ReactNode,
    ports: PortModel[],
    isLoading?: boolean,
}

const PortsCard = ({title, ports, isLoading}: PortsCardProps) => {
    const {yardsByPort} = useYards();
    const {openAddPortModal, openEditPortModal} = usePortModals();
    const {openAddYardModal, openEditYardModal} = useYardModals();
    const my = useRecoilValue(auth);
    const {t} = useTranslation();

    const addPortButton = my.permissions.has('ports create') ? (
        <IconButton
            aria-label="add port"
            onClick={() => openAddPortModal(Boolean(isLoading))}
        >
            <Add/>
        </IconButton>
    ) : null;

    const addYardButton = (port: PortModel) => my.permissions.has('yards create') ? (
        <IconButton
            aria-label="add yard"
            onClick={() => openAddYardModal(port.id, Boolean(isLoading))}
        >
            <Add/>
        </IconButton>
    ) : null;

    return (
        <Card>
            <CardHeader
                title={title}
                action={addPortButton}
            />

            <List>
                {ports.map(port => (
                    <Fragment key={port.id}>
                        <StyledListItem
                            equalWidth
                            secondaryAction={addYardButton(port)}
                        >
                            <ListItemText
                                primary={port.name}
                                onClick={() => openEditPortModal(port)}
                            />
                            <ListItemText
                                primary={port.code || <>&nbsp;</>}
                                onClick={() => openEditPortModal(port)}
                            />
                        </StyledListItem>

                        {yardsByPort[port.id]?.map(yard => (
                            <StyledListItem
                                key={yard.id}
                            >
                                <ListItemIcon>
                                    <SubdirectoryArrowRight/>
                                </ListItemIcon>
                                <ListItemText
                                    primary={<>
                                        {yard.name}
                                        {' '}
                                        {!!yard.isDock && (
                                            <Box component="small" color="info.main">{t('is_dock')}</Box>
                                        )}
                                        {yard.deletedAt && (
                                            <Box component="small" color="text.disabled">{t('disabled')}</Box>
                                        )}
                                    </>}
                                    secondary={yard.phone}
                                    onClick={() => openEditYardModal(yard)}
                                />
                                <ListItemText
                                    sx={{textAlign: 'right'}}
                                    onClick={() => openEditYardModal(yard)}
                                    primary={<>
                                        <Copy value={yard.address}/>
                                        {' '}
                                        <ShareButton
                                            icon={ShareLocation}
                                            size="sm"
                                            title={t('location')}
                                            url={`https://www.google.com/maps/search/?api=1&query=${yard.coordinates.latitude}%2C${yard.coordinates.longitude}`}
                                        />
                                        {' '}
                                        <ViewYardInstructions
                                            yard={yard}
                                        />
                                    </>}
                                />
                            </StyledListItem>
                        ))}
                    </Fragment>
                ))}
            </List>
        </Card>
    );
};

export default PortsCard;
